import { EventEmitter } from 'eventemitter3';
import { walletHubCenterStorageKey } from '../../utils/constants';
import { cacheObj, getCacheObj } from '../misc';
import { ChainTypes } from './types';
import { onlyCallOnceInOneTick } from '@web3/utils';

export type ConnectorEvent = {
  walletChange: [string | undefined, number | undefined];
  addressChange: [string | undefined];
  chainIdChange: [number | undefined];
};

/**
 * 通用的钱包连接器接口，未来BTC、Tron 等其它钱包 均基于这个进行扩展，
 * 一个Connector 代表的是连接了一个钱包地址
 * 实现的时候，需要在accountChanged的时候，清除本地数据
 */
export class AbstractWalletConnector extends EventEmitter<ConnectorEvent> {
  protected _address?: string;
  private _prevAddress?: string;
  public get address() {
    return this._address;
  }
  protected set address(a: string | undefined) {
    this._prevAddress = this._address;
    this._address = a;
    if (this._address !== this._prevAddress) {
      if (this._prevAddress) {
        // 清空数据
        this.setData(undefined, true);
      }
      this._emitAddressChange();
      this._emitWalletChange();
    }
  }

  public publicKey?: string;

  // 地址类型，对于BTC来说，例如：Taproot、segwit之类的
  public addressType = '';

  // 内部特殊使用在类外面设置地址
  public _innerUseSetAddress(address?: string, publicKey?: string) {
    this.address = address;
    this.publicKey = publicKey;
  }

  // chainId，主要还是在EVM上用，其它链一般用不着，可以设置为系统内自定的chainId
  protected _chainId?: number;
  private _prevChainId?: number;
  public get chainId() {
    return this._chainId;
  }
  protected set chainId(a: number | undefined) {
    this._prevChainId = this._chainId;
    this._chainId = a;
    if (this._chainId !== this._prevChainId) {
      this._emitChainIdChange();
      this._emitWalletChange();
    }
  }
  // 子类一定要设置这个值
  readonly chainType!: ChainTypes;
  // connector 的名称
  name: string;

  get storageKey(): string {
    return [walletHubCenterStorageKey, this.name].join('-');
  }

  constructor(name: string) {
    super();
    this.name = name;
  }

  // 主动发起请求与钱包建立连接，并监听事件,  recovery：true 的动作是，尝试使用非用户感知的方式获取用户账户信息，并监听后续账户变化等事件
  async connect(_recovery?: boolean, ..._args: any[]): Promise<string | undefined> {
    requireImplemented('connect');
    return;
  }

  // 断开与钱包的连接
  async disconnect(): Promise<void> {
    requireImplemented('disconnect');
  }

  // 是否可用，即例如钱包是否有安装相关的检测
  async isAvailable(): Promise<boolean> {
    requireImplemented('isAvailable');
    return false;
  }

  // 获取一个操作钱包的SDK
  getProvider(): unknown {
    requireImplemented('getProvider');
    return;
  }

  get data() {
    return this.getData();
  }

  getData(): Record<string, any> {
    return getCacheObj(this.storageKey, this.address) || {};
  }

  // 设置这个钱包 的数据
  setData(data?: Record<string, any>, replace?: boolean): void {
    if (replace) {
      cacheObj(data, this.storageKey, { id: this.address });
    } else {
      const prev = this.getData();
      const newData = { ...prev, ...data };
      cacheObj(newData, this.storageKey, { id: this.address });
    }
  }

  protected _emitWalletChange = onlyCallOnceInOneTick(() => {
    this.emit('walletChange', this.address, this.chainId);
  });
  protected _emitAddressChange = onlyCallOnceInOneTick(() => {
    this.emit('addressChange', this.address);
  });
  protected _emitChainIdChange = onlyCallOnceInOneTick(() => {
    this.emit('chainIdChange', this.chainId);
  });
}

const requireImplemented = (m: string) => {
  throw new Error(`${m} is required to be implemented`);
};
